import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebase';
import { UserProvider } from './contexts/UserContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Dashboard from './components/Dashboard';
import AuthPage from './components/Auth/AuthPage';
import LandingPage from './components/LandingPage';
import SupportPage from './components/pages/SupportPage';
import TermsOfServicePage from './components/pages/TermsOfServicePage';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import RefundPolicyPage from './components/pages/RefundPolicyPage';
import FeedbackPage from './components/pages/FeedbackPage';
import FeedbackThanksPage from './components/pages/FeedbackThanksPage';
import CheckoutCompletePage from './components/pages/CheckoutCompletePage';
import { themeService } from './services/themeService';
import { setAnalyticsUserId, trackPageView, trackEvent } from './services/analyticsService';

// Admin emails that bypass email verification
const ADMIN_BYPASS_EMAILS = [
  'marik17@gmail.com',
  'marik171701@gmail.com',
  // Add your admin email here
];

// Public Layout Component (for unauthenticated pages with navigation)
const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900">
      {/* Simple Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-800/80 backdrop-blur-lg border-b border-system-blue/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors font-rajdhani"
            >
              <span>←</span> Back to Home
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/auth')}
                className="font-rajdhani text-sm font-medium px-4 py-2 text-gray-300 hover:text-white transition-all"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="font-rajdhani text-sm font-semibold px-5 py-2 bg-gradient-to-r from-system-blue to-blue-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] transition-all"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="pt-24 px-4 max-w-7xl mx-auto">
        {children}
      </div>
      
      {/* Footer */}
      <footer className="bg-dark-900 border-t border-gray-800 py-8 sm:py-10 md:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Brand */}
            <div className="col-span-1 sm:col-span-2">
              <div className="mb-3 sm:mb-4">
                <img 
                  src="/logo/logo.png" 
                  alt="AwakenArc Logo" 
                  className="h-12 sm:h-14 md:h-16 w-auto object-contain mb-2 sm:mb-3 opacity-80"
                />
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-orbitron text-xl sm:text-2xl font-bold text-white">
                    Awaken<span className="text-system-blue">Arc</span>
                  </span>
                </div>
              </div>
              <p className="font-rajdhani text-sm sm:text-base text-gray-400 max-w-md">
                Transform your life through the power of gamification. Complete quests, level up, and become the ultimate version of yourself.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-orbitron font-bold text-white mb-3 sm:mb-4 text-sm sm:text-base">Product</h4>
              <ul className="space-y-1.5 sm:space-y-2 font-rajdhani text-gray-400 text-sm sm:text-base">
                <li><a href="/#features" className="hover:text-system-blue transition-colors inline-block py-1">Features</a></li>
                <li><a href="/#how-it-works" className="hover:text-system-blue transition-colors inline-block py-1">How It Works</a></li>
                <li><a href="/#pricing" className="hover:text-system-blue transition-colors inline-block py-1">Pricing</a></li>
                <li><a href="/#demo" className="hover:text-system-blue transition-colors inline-block py-1">Demo</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-orbitron font-bold text-white mb-3 sm:mb-4 text-sm sm:text-base">Support</h4>
              <ul className="space-y-1.5 sm:space-y-2 font-rajdhani text-gray-400 text-sm sm:text-base">
                <li>
                  <button 
                    onClick={() => navigate('/support')} 
                    className="hover:text-system-blue transition-colors text-left py-1"
                  >
                    Help Center
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/support')} 
                    className="hover:text-system-blue transition-colors text-left py-1"
                  >
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 sm:pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col items-center md:items-start gap-2 sm:gap-3">
              <img 
                src="/logo/logo.png" 
                alt="AwakenArc Logo" 
                className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-60 transition-opacity duration-300"
              />
              <p className="font-rajdhani text-gray-600 text-xs">
                © 2025 AwakenArc. All rights reserved.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 font-rajdhani text-gray-500 text-xs sm:text-sm">
              <button 
                onClick={() => navigate('/privacy')}
                className="hover:text-system-blue transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="hover:text-system-blue transition-colors"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => navigate('/refund')}
                className="hover:text-system-blue transition-colors"
              >
                Refund Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for demo mode first
    const demoMode = sessionStorage.getItem('is_demo_mode') === 'true';
    setIsDemoMode(demoMode);

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      
      // 🔧 FIX: Clear demo mode when user authenticates
      if (currentUser) {
        // Clear demo mode state when user signs in/up
        const isDemoMode = sessionStorage.getItem('is_demo_mode') === 'true';
        if (isDemoMode) {
          sessionStorage.removeItem('is_demo_mode');
          sessionStorage.removeItem('demo_user');
          sessionStorage.removeItem('demo_session_start_time');
          localStorage.removeItem('demo_background_settings');
          setIsDemoMode(false);
          
          // Track demo to authenticated conversion
          trackEvent('demo_to_authenticated_conversion', {
            conversion_type: 'demo_to_auth',
            user_authenticated: true
          });
          
          // 🔧 AUTO-REFRESH: Force page refresh to clear demo mode completely
          setTimeout(() => {
            window.location.reload();
          }, 100); // Small delay to ensure state is cleared
        }
        
        setAnalyticsUserId(currentUser.uid);
        trackPageView('authenticated', 'Authenticated User');
      } else {
        setAnalyticsUserId(null);
        // Only track page view for non-landing pages to avoid duplicates
        if (location.pathname !== '/') {
          trackPageView(location.pathname);
        }
      }
    });

    return () => unsubscribe();
  }, [location]);

  // Check if user is admin (bypass email verification)
  const isAdminUser = (user: User | null): boolean => {
    if (!user?.email) return false;
    return ADMIN_BYPASS_EMAILS.includes(user.email.toLowerCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-float">⚔️</div>
          <div className="flex items-center justify-center gap-3">
            <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
            <span className="font-orbitron text-sm tracking-wider text-system-blue uppercase">
              ［ System Loading ］
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/dashboard" replace />} />
        <Route path="/auth" element={!user ? <AuthPage onAuthSuccess={() => {}} onBack={() => navigate('/')} /> : <Navigate to="/dashboard" replace />} />
        
        {/* Public Policy Routes with Layout */}
        <Route path="/terms" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
        <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
        <Route path="/refund" element={<PublicLayout><RefundPolicyPage /></PublicLayout>} />
        <Route path="/support" element={<PublicLayout><SupportPage /></PublicLayout>} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/feedback/thanks" element={<FeedbackThanksPage />} />
        <Route path="/checkout/complete" element={<CheckoutCompletePage />} />
        
        {/* Protected Routes */}
        {(user || isDemoMode) ? (
          <>
            <Route
              path="/dashboard"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/stats"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="stats"
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/shop"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="shop"
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/history"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="history"
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/achievements"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="achievements"
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="settings"
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/calendar"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="calendar"
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/focus"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="focus"
                  />
                </UserProvider>
              }
            />
            <Route
              path="/dashboard/passive-skills"
              element={
                <UserProvider user={user || undefined}>
                  <Dashboard
                    showEmailVerificationBanner={user ? (!user.emailVerified && !isAdminUser(user)) : false}
                    user={user}
                    initialPage="passiveSkills"
                  />
                </UserProvider>
              }
            />
          </>
        ) : (
          <Route path="/dashboard/*" element={<Navigate to="/auth" replace />} />
        )}
        
        {/* Fallback - redirect to home or dashboard based on auth */}
        <Route path="*" element={<Navigate to={(user || isDemoMode) ? "/dashboard" : "/"} replace />} />
      </Routes>
    </LanguageProvider>
  );
}

export default App;
